'use client'

import type { Data } from '@repo/strapi'
import { WistiaPlayer } from '@wistia/wistia-player-react'
import type { ComponentProps } from 'react'
import ReactPlayer from 'react-player'
import { Container, type ContainerProps } from '@/components/elementary/Container'
import { getAspectRatioAttribute } from '@/lib/image-helpers'
import { formatImageUrl } from '@/lib/strapi-helpers'
import type { LayoutContext } from '@/types/general'

type VideoProps = {
	readonly component?: Data.Component<'shared.video'> | null
	readonly className?: string
	readonly reactPlayerProps?: ComponentProps<typeof ReactPlayer>
	readonly layoutContext?: LayoutContext
	readonly containerPaddingX?: ContainerProps['paddingX']
	readonly containerPaddingY?: ContainerProps['paddingY']
	readonly containerPaddingTop?: ContainerProps['paddingTop']
	readonly containerPaddingBottom?: ContainerProps['paddingBottom']
}

// Convert YouTube URLs to privacy-enhanced (nocookie) domain
function convertToYouTubeNoCookie(url: string): string {
	if (typeof url !== 'string') return url

	// Handle all common YouTube URL formats
	return url
		.replace('https://www.youtube.com/', 'https://www.youtube-nocookie.com/')
		.replace('https://youtube.com/', 'https://www.youtube-nocookie.com/')
}

function getYouTubeThumbnail(url: string): string | false {
	if (typeof url !== 'string') return false

	const noCookieUrl = convertToYouTubeNoCookie(url)

	// Extract video ID from nocookie URL
	const match = noCookieUrl.match(/youtube-nocookie\.com\/(?:embed\/|watch\?v=)([^?&\s]+)/)

	if (match?.[1]) {
		return `https://i.ytimg.com/vi/${match[1]}/maxresdefault.jpg`
	}

	return false
}

export const Video = ({
	component,
	className,
	reactPlayerProps,
	layoutContext,
	containerPaddingX,
	containerPaddingY,
	containerPaddingTop,
	containerPaddingBottom,
}: VideoProps) => {
	if (!component) {
		return null
	}

	const rawSrc = component.videoLink ?? String(formatImageUrl(component.media?.url))
	const src = convertToYouTubeNoCookie(rawSrc)
	const aspectRatio = getAspectRatioAttribute(component.aspectRatio).aspectRatio

	const autoPlay = Boolean(component.autoplay)
	const loop = Boolean(component.loop)
	const muted = autoPlay ? true : Boolean(component.muted)
	const playsInline = Boolean(component.playsInline)
	const preload = component.preload ?? 'none'
	const controls = Boolean(component.showControls)

	const player = component.wistiaId ? (
		<WistiaPlayer
			id={component.anchorId ?? ''}
			className={className}
			style={{
				width: '100%',
				height: 'auto',
				aspectRatio,
			}}
			mediaId={component.wistiaId}
			aspect={16 / 9}
			autoplay={autoPlay}
			muted={muted}
			preload={preload}
			wistiaPopover={component.wistiaPopover ?? false}
			popoverAnimateThumbnail={component.wistiaPopover ?? false}
		/>
	) : (
		<ReactPlayer
			{...reactPlayerProps}
			src={src}
			id={component.anchorId ?? ''}
			className={className}
			style={{
				width: '100%',
				height: 'auto',
				aspectRatio,
				...reactPlayerProps?.style,
			}}
			playing={autoPlay}
			loop={loop}
			muted={muted}
			playsInline={playsInline}
			preload={preload}
			controls={controls}
			light={getYouTubeThumbnail(src)}
			config={{
				// Supported parameters: https://developers.google.com/youtube/player_parameters#Parameters
				youtube: {
					enablejsapi: 0,
					rel: 0,
				},
			}}
		/>
	)

	const videoFrame = (
		<div className="relative w-full" style={{ aspectRatio: aspectRatio }}>
			<div className="absolute inset-0">{player}</div>
		</div>
	)

	if (layoutContext === 'landing' || layoutContext === 'apps-category') {
		return (
			<Container
				paddingX={containerPaddingX}
				paddingY={containerPaddingY}
				paddingTop={containerPaddingTop}
				paddingBottom={containerPaddingBottom}
				className="w-full mx-auto lg:max-w-212.5"
			>
				{videoFrame}
			</Container>
		)
	}

	return videoFrame
}
